import { useState, useEffect } from "react";
import { Copy, Heart, Check, ChevronDown, ExternalLink, MoreHorizontal, Sparkles, Globe } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { AI_TOOLS, MORE_AI_TOOLS, buildHelperPrompt, LANGUAGES, getOutputLanguage, setOutputLanguage, withLanguage } from "../lib/aiLaunch";
import { hasProfanity } from "../lib/profanity";

export const CommandCard = ({ cmd, onToggleFavourite, searchPhrase, onRelatedClick }) => {
  const [exampleIdx, setExampleIdx] = useState(0);
  const [copied, setCopied] = useState(null);
  const [fav, setFav] = useState(cmd.is_favourite);
  const [moreOpen, setMoreOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [lang, setLang] = useState(getOutputLanguage());

  useEffect(() => {
    const sync = (e) => setLang(e.detail);
    window.addEventListener("ai-language-changed", sync);
    return () => window.removeEventListener("ai-language-changed", sync);
  }, []);

  const track = (action) => {
    api.post(`/commands/${cmd.slug}/track`, { action, search_phrase: searchPhrase || null }).catch(() => {});
  };

  const copyText = async (text, which, action) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(which);
    toast.success("Copied!");
    track(action);
    setTimeout(() => setCopied(null), 1600);
  };

  const launchIn = (tool, customPrompt, action) => {
    setMoreOpen(false);
    const prompt = withLanguage(customPrompt || `${cmd.name} ${cmd.examples?.[exampleIdx] || ""}`.trim());
    window.open(tool.url(prompt), "_blank", "noopener");
    navigator.clipboard?.writeText(prompt).catch(() => {});
    toast.success(tool.prefill
      ? `Opened in ${tool.label} — your command is pre-filled`
      : `${tool.label} opened — command copied, just paste it (Ctrl+V)`);
    track(action || `launch_${tool.key}`);
  };

  const assistLaunch = (tool) => {
    if (!notes.trim()) { toast.error("Describe what you need first"); return; }
    if (hasProfanity(notes)) { toast.error("Please remove inappropriate language — see our Acceptable Use policy"); return; }
    launchIn(tool, buildHelperPrompt(cmd.name, notes, cmd.category, cmd.examples?.[exampleIdx] || ""), `assist_${tool.key}`);
  };

  const toggleFav = async () => {
    const next = !fav;
    setFav(next);
    try {
      if (next) await api.post(`/favourites/${cmd.slug}`);
      else await api.delete(`/favourites/${cmd.slug}`);
      toast.success(next ? "Saved to favourites" : "Removed from favourites");
      onToggleFavourite && onToggleFavourite(cmd.slug, next);
    } catch {
      setFav(!next);
    }
  };

  return (
    <div data-testid={`command-card-${cmd.slug}`}
      className="bg-white border border-slate-200/80 shadow-sm hover:shadow-md hover:border-emerald-500/40 transition-all rounded-2xl p-5 flex flex-col gap-3.5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <span className="cmd-chip bg-slate-900 text-amber-400 font-bold px-3 py-1.5 rounded-lg text-sm inline-block">{cmd.name}</span>
          <div className="text-xs text-slate-400 mt-2 font-medium">{cmd.category}</div>
        </div>
        <button data-testid={`save-command-btn-${cmd.slug}`} onClick={toggleFav} aria-label="Save"
          className={`p-2.5 rounded-full transition-all ${fav ? "bg-red-50 text-red-500" : "bg-slate-50 text-slate-400 hover:text-red-400"}`}>
          <Heart size={18} fill={fav ? "currentColor" : "none"} />
        </button>
      </div>

      {cmd.examples?.length > 0 && (
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Choose an example:</label>
          <div className="relative mt-1.5">
            <select data-testid={`example-select-${cmd.slug}`} value={exampleIdx}
              onChange={(e) => setExampleIdx(Number(e.target.value))}
              className="w-full appearance-none bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 pr-9 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/40">
              {cmd.examples.map((ex, i) => (
                <option key={i} value={i}>{ex.length > 70 ? ex.slice(0, 70) + "…" : ex}</option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
          <details className="mt-2 group" data-testid={`example-preview-${cmd.slug}`}>
            <summary data-testid={`example-preview-toggle-${cmd.slug}`} className="cursor-pointer text-xs font-semibold text-emerald-700 hover:text-emerald-900 transition-colors">Read full example</summary>
            <p data-testid={`full-example-${cmd.slug}`} className="mt-2 text-sm text-slate-600 leading-relaxed whitespace-pre-wrap break-words">{cmd.examples[exampleIdx]}</p>
          </details>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
        <button data-testid={`copy-command-btn-${cmd.slug}`}
          onClick={() => copyText(cmd.name, "cmd", "copied")}
          className="flex-1 min-w-[130px] bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-3 py-2.5 rounded-xl flex items-center justify-center gap-1.5 active:scale-[0.98] transition-all">
          {copied === "cmd" ? <Check size={15} /> : <Copy size={15} />} COPY COMMAND
        </button>
        <button data-testid={`copy-command-example-btn-${cmd.slug}`}
          onClick={() => copyText(withLanguage(`${cmd.name} ${cmd.examples?.[exampleIdx] || ""}`.trim()), "both", "copied_example")}
          className="flex-1 min-w-[150px] bg-slate-900 hover:bg-slate-800 text-amber-400 text-sm font-semibold px-3 py-2.5 rounded-xl flex items-center justify-center gap-1.5 active:scale-[0.98] transition-all">
          {copied === "both" ? <Check size={15} /> : <Copy size={15} />} + EXAMPLE
        </button>
      </div>

      <div className="pt-2 border-t border-slate-100">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Use in:</span>
          <label className="flex items-center gap-1 text-[11px] font-semibold text-slate-400">
            <Globe size={11} /> AI reply in:
            <select data-testid={`language-select-${cmd.slug}`} value={lang}
              onChange={(e) => { setLang(e.target.value); setOutputLanguage(e.target.value); if (e.target.value !== "English") toast.success(`AI replies will be in ${e.target.value}`); }}
              className="bg-slate-50 border border-slate-200 rounded-full px-2 py-1 text-[11px] font-semibold text-slate-600 focus:outline-none focus:ring-1 focus:ring-emerald-400 cursor-pointer">
              {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 mt-1.5 relative">
          {AI_TOOLS.map((tool) => (
            <button key={tool.key} data-testid={`launch-${tool.key}-btn-${cmd.slug}`} onClick={() => launchIn(tool)}
              className="flex items-center gap-1 bg-slate-50 hover:bg-emerald-50 border border-slate-200 hover:border-emerald-400 text-slate-700 hover:text-emerald-700 text-xs font-semibold px-2.5 py-1.5 rounded-full transition-all active:scale-[0.97]">
              <ExternalLink size={11} /> {tool.label}
            </button>
          ))}
          <button data-testid={`launch-more-btn-${cmd.slug}`} onClick={() => setMoreOpen(!moreOpen)}
            className="flex items-center gap-1 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-500 text-xs font-semibold px-2.5 py-1.5 rounded-full transition-all">
            <MoreHorizontal size={13} /> More
          </button>
          {moreOpen && (
            <div data-testid={`launch-more-menu-${cmd.slug}`}
              className="absolute top-full right-0 mt-1 z-20 bg-white border border-slate-200 rounded-xl shadow-lg p-1.5 flex flex-col min-w-[140px]">
              {MORE_AI_TOOLS.map((tool) => (
                <button key={tool.key} data-testid={`launch-${tool.key}-btn-${cmd.slug}`} onClick={() => launchIn(tool)}
                  className="flex items-center gap-1.5 text-left text-xs font-semibold text-slate-600 hover:text-emerald-700 hover:bg-emerald-50 px-3 py-2 rounded-lg transition-colors">
                  <ExternalLink size={11} /> {tool.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        <button data-testid={`assist-toggle-btn-${cmd.slug}`} onClick={() => setHelpOpen(!helpOpen)}
          className="flex items-center gap-1.5 text-xs font-bold text-amber-600 hover:text-amber-700 transition-colors">
          <Sparkles size={13} /> Help me fill this in {helpOpen ? "▴" : "▾"}
        </button>
        {helpOpen && (
          <div data-testid={`assist-panel-${cmd.slug}`} className="mt-2 bg-amber-50/70 border border-amber-200/80 rounded-xl p-3">
            <p className="text-xs text-slate-500 font-medium">Describe what you need in your own words — your AI will polish the details and do the task in one go.</p>
            <textarea data-testid={`assist-notes-input-${cmd.slug}`} value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              placeholder={'e.g. "customer hasn\'t paid, don\'t want to sound rude"'}
              className="w-full mt-2 bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-400/50 resize-none" />
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              {AI_TOOLS.map((tool) => (
                <button key={tool.key} data-testid={`assist-${tool.key}-btn-${cmd.slug}`} onClick={() => assistLaunch(tool)}
                  className="flex items-center gap-1 bg-white hover:bg-amber-100 border border-amber-300 text-amber-800 text-xs font-semibold px-2.5 py-1.5 rounded-full transition-all active:scale-[0.97]">
                  <ExternalLink size={11} /> {tool.label}
                </button>
              ))}
              <button data-testid={`assist-copy-btn-${cmd.slug}`}
                onClick={() => { if (!notes.trim()) { toast.error("Describe what you need first"); return; } if (hasProfanity(notes)) { toast.error("Please remove inappropriate language — see our Acceptable Use policy"); return; } copyText(withLanguage(buildHelperPrompt(cmd.name, notes, cmd.category, cmd.examples?.[exampleIdx] || "")), "assist", "assist_copied"); }}
                className="flex items-center gap-1 bg-slate-900 hover:bg-slate-800 text-amber-400 text-xs font-semibold px-2.5 py-1.5 rounded-full transition-all active:scale-[0.97]">
                {copied === "assist" ? <Check size={11} /> : <Copy size={11} />} Copy
              </button>
            </div>
          </div>
        )}
      </div>

      {cmd.related?.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <span className="text-slate-400 font-medium">Related:</span>
          {cmd.related.slice(0, 2).map((r) => (
            <button key={r} data-testid={`related-cmd-${r}`} onClick={() => onRelatedClick && onRelatedClick(r)}
              className="text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2 py-1 rounded-md font-semibold transition-colors">/{r}</button>
          ))}
        </div>
      )}
    </div>
  );
};
